import React, { ReactElement, useMemo } from 'react'
import { Row, Col, Card } from 'react-bootstrap'
import { IBroadcastFormRecipientResponseReportRow } from 'types/BroadcastForm'
import { calculateNPS } from 'utils/npsCalculation'
import { SurveyResponseDistributionChart } from './components/SurveyResponseDistributionChart'
import { ResponseTimelineChart } from './components/ResponseTimelineChart'
import { WordCloudChart } from './components/WordCloudChart'

interface EnterpriseSurveyReportOverviewProps {
  filteredRecipients: IBroadcastFormRecipientResponseReportRow[]
}

export const EnterpriseSurveyReportOverview: React.FC<EnterpriseSurveyReportOverviewProps> = ({
  filteredRecipients,
}): ReactElement => {
  const metrics = useMemo(() => {
    // Deduplicate by recipient (ProfileId + FormSessionId) since filteredRecipients
    // contains one row per question response, not per recipient
    const recipientMap = new Map<string, IBroadcastFormRecipientResponseReportRow>()

    filteredRecipients.forEach((recipient) => {
      const key = `${recipient.ProfileId}-${recipient.FormSessionId}`
      if (!recipientMap.has(key)) {
        recipientMap.set(key, recipient)
      } else {
        // If we already have this recipient, keep the one with HasResponse=true if available
        const existing = recipientMap.get(key)
        if (existing && !existing.HasResponse && recipient.HasResponse) {
          recipientMap.set(key, recipient)
        }
      }
    })

    const uniqueRecipients = Array.from(recipientMap.values())
    const totalRecipients = uniqueRecipients.length
    const responses = uniqueRecipients.filter((r) => r.HasResponse).length
    const unresponses = totalRecipients - responses
    const responseRate = totalRecipients > 0 ? (responses / totalRecipients) * 100 : 0

    return {
      totalRecipients,
      responses,
      unresponses,
      responseRate,
    }
  }, [filteredRecipients])

  const textResponses = useMemo(
    () => filteredRecipients
      .filter(
        (r) => r.HasResponse
          && r.ResponseText != null
          && r.ResponseText.trim() !== '',
      )
      .map((r) => r.ResponseText as string),
    [filteredRecipients],
  )

  // Calculate NPS from Net Promoter responses
  const npsScore = useMemo(() => {
    // Filter to only responses (not all recipients)
    const responses = filteredRecipients.filter((r) => r.HasResponse)
    return calculateNPS(responses)
  }, [filteredRecipients])

  return (
    <div>
      <div className="mt-4 mb-4 d-flex flex-wrap" style={{ gap: '1rem' }}>
        <Card style={{ flex: '1 1 200px', minWidth: '200px' }}>
          <Card.Body>
            <Card.Title className="text-muted small mb-2">Total Sent</Card.Title>
            <Card.Text className="h3 mb-0">{metrics.totalRecipients.toLocaleString()}</Card.Text>
          </Card.Body>
        </Card>
        <Card style={{ flex: '1 1 200px', minWidth: '200px' }}>
          <Card.Body>
            <Card.Title className="text-muted small mb-2">Responded</Card.Title>
            <Card.Text className="h3 mb-0 text-success">{metrics.responses.toLocaleString()}</Card.Text>
          </Card.Body>
        </Card>
        <Card style={{ flex: '1 1 200px', minWidth: '200px' }}>
          <Card.Body>
            <Card.Title className="text-muted small mb-2">Not Responded</Card.Title>
            <Card.Text className="h3 mb-0 text-warning">{metrics.unresponses.toLocaleString()}</Card.Text>
          </Card.Body>
        </Card>
        <Card style={{ flex: '1 1 200px', minWidth: '200px' }}>
          <Card.Body>
            <Card.Title className="text-muted small mb-2">Response Rate</Card.Title>
            <Card.Text className="h3 mb-0 text-primary">
              {metrics.responseRate.toFixed(1)}
              %
            </Card.Text>
          </Card.Body>
        </Card>
        {npsScore != null && (
          <Card style={{ flex: '1 1 200px', minWidth: '200px' }}>
            <Card.Body>
              <Card.Title className="text-muted small mb-2">Net Promoter Score</Card.Title>
              <Card.Text className="h3 mb-0 text-info">
                {npsScore}
              </Card.Text>
            </Card.Body>
          </Card>
        )}
      </div>

      <Row className="mt-4">
        <Col md={6}>
          <Card>
            <Card.Header>
              <h5 className="mb-0">Response Timeline</h5>
            </Card.Header>
            <Card.Body>
              <ResponseTimelineChart filteredRecipients={filteredRecipients} />
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card>
            <Card.Header>
              <h5 className="mb-0">Word Cloud</h5>
            </Card.Header>
            <Card.Body>
              <WordCloudChart responseTexts={textResponses} />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card className="mt-4">
        <Card.Header>
          <h5 className="mb-0">Response Distribution by Question</h5>
        </Card.Header>
        <Card.Body>
          <SurveyResponseDistributionChart filteredRecipients={filteredRecipients} />
        </Card.Body>
      </Card>
    </div>
  )
}
